import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { AgentThread } from "@/AgentThread/AgentThreadSidebar/AgentThread";
import { AgentThreadByIdView } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView";
import { AIAgentThreadProvider } from "@/AgentThread/AgentThreadSidebar/AIAgentThreadContext";
import { useAuth } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { useChatDebugSummary } from "@/shared/domain/admin/hooks/useChatDebugSummary";
import { isActiveThreadStatus } from "@/shared/domain/agentThread/AgentThreadRecord";
import { Button } from "@/shared/foundation/primitives";

import styles from "./AdminAgentThreadViewer.module.css";
import { BedrockPromptView } from "./BedrockPromptView";
import { CopyLinkButton } from "./CopyLinkButton";
import { MessageDebugButton } from "./MessageDebugButton";
import { ReadOnlyAIAgentThreadProvider } from "./ReadOnlyAIAgentThreadProvider";

interface AdminAgentThreadViewerProps {
  threadId: string;
  onBack: () => void;
  /** Sequence number of the message to scroll to once the thread renders. */
  scrollToMessageSequenceNum?: number;
  onViewFeedbackForAgentThread?: () => void;
  /** Invoked when an embedded child of the viewer switches to a different
   *  thread. Fires from the interactive branch when the admin picks a thread from
   *  the history pane, and from the read-only branch when a fork succeeds
   *  (navigating the viewer to the admin's freshly created copy). */
  onAgentThreadSelected?: (threadId: string) => void;
  /** Fully-qualified URL to copy when the admin hits the header share
   *  button. Parent decides whether this points at the thread or at a
   *  specific feedback entry so the link matches the context the admin is
   *  viewing in. */
  shareUrl: string;
}

export function AdminAgentThreadViewer({
  threadId,
  onBack,
  scrollToMessageSequenceNum,
  onViewFeedbackForAgentThread,
  onAgentThreadSelected,
  shareUrl,
}: AdminAgentThreadViewerProps) {
  const { agentThreadService } = useDomainServices();
  const { currentUser } = useAuth();
  const {
    data: agentThread,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["adminAgentThread", threadId],
    queryFn: ({ signal }) =>
      agentThreadService.getAgentThreadById(threadId, true, {
        abortSignal: signal,
      }),
    enabled: !!threadId,
    // Poll while the thread is still in flight so newly-finished messages
    // appear in the viewer (and, via the effect below, so their debug controls
    // light up) without the admin having to reload. Stops on every
    // idle/terminal state, including the permanent GOALS_FAILED, mirroring
    // useActiveAgentThread.
    refetchInterval: (query) =>
      isActiveThreadStatus(query.state.data?.status) ? 1500 : false,
  });
  const containerRef = React.useRef<HTMLDivElement>(null);

  // When set, the conversation is replaced by the reconstructed Bedrock view
  // for that assistant message. Null = normal conversation view.
  const [debuggingSequenceNum, setDebuggingSequenceNum] = React.useState<
    number | null
  >(null);

  // Drives which messages get an inline debug control: only assistant
  // messages produced by a recorded Bedrock call have an invocation.
  const { data: debugSummary, refetch: refetchDebugSummary } =
    useChatDebugSummary(threadId);

  // The debug summary is captured server-side only after a Bedrock call
  // finishes, so a message that's still generating has no invocation row yet
  // and gets no debug control. Each time the message count changes (a new
  // message finished while the thread polls above), refetch the summary so the
  // freshly-recorded invocation surfaces its control immediately, rather than
  // only after a manual reload.
  const messageCount = agentThread?.messages.length;
  React.useEffect(() => {
    if (messageCount !== undefined) {
      void refetchDebugSummary();
    }
  }, [messageCount, refetchDebugSummary]);
  const debuggableSequenceNums = React.useMemo(
    () =>
      new Set(
        debugSummary?.invocations.map(
          (invocation) => invocation.message_sequence_num,
        ) ?? [],
      ),
    [debugSummary],
  );

  // Rendered under every message that was produced by a recorded Bedrock call.
  // Passed into both thread providers so it reaches the message rows whether
  // the admin's own thread (interactive) or another user's (read-only).
  const renderMessageDebugControl = React.useCallback(
    (messageSequenceNum: number) =>
      debuggableSequenceNums.has(messageSequenceNum) ? (
        <MessageDebugButton
          onActivate={() => {
            setDebuggingSequenceNum(messageSequenceNum);
          }}
        />
      ) : null,
    [debuggableSequenceNums],
  );

  React.useEffect(() => {
    containerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    // Leaving the debug view behind when the admin switches threads.
    setDebuggingSequenceNum(null);
  }, [threadId]);

  // Scroll to a specific message once the thread is loaded. We query by
  // ``data-message-sequence-num`` rather than by article position because
  // tool groups, pending tool uses, and approval cards also render as
  // ``<article>`` elements — indexing by position would land on the wrong
  // element. ``requestAnimationFrame`` defers the lookup until after React
  // has committed the DOM (the previous setTimeout(100) was a magic-number
  // race against the same render cycle).
  React.useEffect(() => {
    if (!agentThread || scrollToMessageSequenceNum === undefined) {
      return;
    }
    const handle = window.requestAnimationFrame(() => {
      const target = containerRef.current?.querySelector(
        `[data-message-sequence-num="${scrollToMessageSequenceNum}"]`,
      );
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => window.cancelAnimationFrame(handle);
  }, [agentThread, scrollToMessageSequenceNum]);

  // Memoised so passing the prop doesn't recreate the function each render —
  // the AIAgentThreadProvider effect that watches ``onThreadIdChanged`` would
  // otherwise re-run every render and treat each as a fresh handler.
  const handleAgentThreadIdChangedFromInteractive = React.useCallback(
    (nextId: string | undefined) => {
      if (nextId && nextId !== agentThread?.thread_id) {
        onAgentThreadSelected?.(nextId);
      }
    },
    [agentThread?.thread_id, onAgentThreadSelected],
  );

  // ``currentUser`` is ``null`` while auth is loading (never ``undefined``).
  // Treat the branch decision as "unresolved" until both the thread and the
  // user are loaded — the previous code briefly flashed the read-only
  // viewer and banner on the admin's own thread until auth resolved.
  const ownerCheckResolved = agentThread !== undefined && currentUser !== null;
  const isOwnAgentThread =
    ownerCheckResolved && agentThread.created_by === currentUser.user_id;

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.header}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
        <CopyLinkButton
          url={shareUrl}
          tooltip="Copy a share link to this view"
        />
        {onViewFeedbackForAgentThread && (
          <Button
            size="small"
            variant="outlined"
            onClick={onViewFeedbackForAgentThread}
          >
            View feedback for this thread
          </Button>
        )}
        {agentThread && (
          <div className={styles.metadata}>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Org:</span>
              <span className={styles.metadataValue}>{agentThread.org_id}</span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>User:</span>
              <span className={styles.metadataValue}>
                {agentThread.created_by}
              </span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Created:</span>
              <span className={styles.metadataValue}>
                {new Date(agentThread.created).toLocaleString()}
              </span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Model:</span>
              <span className={styles.metadataValue}>
                {agentThread.model_profile || "default"}
              </span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Messages:</span>
              <span className={styles.metadataValue}>
                {agentThread.messages.length}
              </span>
            </div>
            {agentThread.forked_from_thread_id && (
              <div className={styles.metadataItem}>
                <span className={styles.metadataLabel}>Forked from:</span>
                <span className={styles.metadataValue}>
                  {agentThread.forked_from_thread_id}
                  {agentThread.forked_from_message_sequence_num !== null &&
                  agentThread.forked_from_message_sequence_num !== undefined
                    ? ` @ msg ${agentThread.forked_from_message_sequence_num}`
                    : ""}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.agentThreadContent}>
        {isLoading && (
          <div className={styles.loadingContainer}>
            <CircularProgress size="1.5rem" />
          </div>
        )}
        {error && (
          <div className={styles.errorContainer}>
            Failed to load thread: {error.message}
          </div>
        )}
        {agentThread && debuggingSequenceNum !== null && (
          // Debug view: the conversation is replaced by what Bedrock saw when
          // generating the chosen message. ``onExit`` returns to the thread.
          <BedrockPromptView
            agentThread={agentThread}
            messageSequenceNum={debuggingSequenceNum}
            invocation={debugSummary?.invocations.find(
              (entry) => entry.message_sequence_num === debuggingSequenceNum,
            )}
            onExit={() => {
              setDebuggingSequenceNum(null);
            }}
          />
        )}
        {agentThread &&
          debuggingSequenceNum === null &&
          ownerCheckResolved &&
          isOwnAgentThread && (
            // Admin's own thread — render the full interactive AgentThread so they can
            // continue the conversation in place. ``key`` forces a fresh provider
            // when they jump to a different own-thread so internal state resets.
            <AIAgentThreadProvider
              key={agentThread.thread_id}
              initialThreadId={agentThread.thread_id}
              onThreadIdChanged={handleAgentThreadIdChangedFromInteractive}
              renderMessageDebugControl={renderMessageDebugControl}
            >
              <AgentThread />
            </AIAgentThreadProvider>
          )}
        {agentThread &&
          debuggingSequenceNum === null &&
          ownerCheckResolved &&
          !isOwnAgentThread && (
            // Read-only branch: forks navigate the viewer to the admin's new
            // copy via ``onAgentThreadClicked``. Without this, ``ForkAgentThreadButton``'s
            // success path lands on a no-op and the admin sees no change.
            <ReadOnlyAIAgentThreadProvider
              onAgentThreadClicked={onAgentThreadSelected}
              renderMessageDebugControl={renderMessageDebugControl}
            >
              <AgentThreadByIdView agentThread={agentThread} />
            </ReadOnlyAIAgentThreadProvider>
          )}
      </div>

      {agentThread &&
        debuggingSequenceNum === null &&
        ownerCheckResolved &&
        !isOwnAgentThread && (
          <div className={styles.readOnlyBanner}>
            Read-only view. Fork from any assistant message to continue the
            conversation in your own session.
          </div>
        )}
    </div>
  );
}
